/**
 * 应急 SOS 弹层。
 * 原型只是一个静态弹层，「平均到场 4 小时」「行业平均 72 小时」都是硬编码文案。
 * 此处接通真实的应急事件流转：触发 → 派单 → 在途 → 到场 → 结束。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SosEvent } from '@hew3/shared';
import { ApiError, api } from '../../lib/api.ts';
import { Button, Sheet, StateBox } from '../../components/ui.tsx';
import { useUi } from '../../store/ui.ts';
import { formatDateTime } from '../../lib/format.ts';

export function SosSheet() {
  const open = useUi((s) => s.sheet === 'sos');
  const closeSheet = useUi((s) => s.closeSheet);
  const showToast = useUi((s) => s.showToast);
  const queryClient = useQueryClient();
  const [locationText, setLocationText] = useState('');
  const [note, setNote] = useState('');

  const activeQuery = useQuery({
    queryKey: ['sos', 'active'],
    queryFn: () => api.get<SosEvent | null>('/api/services/sos/active'),
    enabled: open,
  });

  const trigger = useMutation({
    mutationFn: () => api.post<SosEvent>('/api/services/sos', { locationText, note }),
    onSuccess: (event) => {
      showToast(`应急响应已触发，处理人 ${event.handlerName}`);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['sos'] });
      void queryClient.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (err) => showToast(err instanceof ApiError ? err.message : '触发失败，请重试', 'error'),
  });

  const advance = useMutation({
    mutationFn: (vars: { id: string; status: SosEvent['status'] }) =>
      api.patch<SosEvent>(`/api/services/sos/${vars.id}`, { status: vars.status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sos'] });
      void queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });

  const active = activeQuery.data ?? null;
  const nextStatus: Record<SosEvent['status'], SosEvent['status'] | null> = {
    dispatched: 'enroute',
    enroute: 'arrived',
    arrived: 'closed',
    closed: null,
  };

  return (
    <Sheet open={open} title="应急 SOS" onClose={closeSheet}>
      {activeQuery.isLoading && <StateBox kind="loading" title="正在查询应急状态…" />}

      {active && (
        <div className="card" style={{ borderColor: 'rgba(255,93,126,.45)', background: 'rgba(255,93,126,.08)' }}>
          <div className="row between">
            <div className="card-tt bar" style={{ color: 'var(--danger)' }}>
              应急响应进行中
            </div>
            <span className="badge danger">{active.statusLabel}</span>
          </div>
          <div className="divider" />
          <div className="li-s">触发时间：{formatDateTime(active.createdAt)}</div>
          <div className="li-s">处理人：{active.handlerName}</div>
          <div className="li-s">位置：{active.locationText || '未提供'}</div>
          <div className="li-s">预计到场：{active.etaText}</div>
          {nextStatus[active.status] && (
            <>
              <div className="sp12" />
              <Button
                block
                small
                disabled={advance.isPending}
                onClick={() => advance.mutate({ id: active.id, status: nextStatus[active.status] as SosEvent['status'] })}
              >
                推进状态（{active.statusLabel} → 下一阶段）
              </Button>
            </>
          )}
        </div>
      )}

      {!active && !activeQuery.isLoading && (
        <>
          <div className="note-src">
            <b>0.3 秒触发三级响应 · 15 分钟咨询响应 · 2 小时需求确认 · 1 个工作日预约反馈</b>
            <br />
            触发后由您的首席健康管家受理，联动合作医疗机构。平均响应 4 小时（行业平均 72 小时）。
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <label className="field-label" htmlFor="sos-loc">
              当前位置（便于就近调度）
            </label>
            <input
              id="sos-loc"
              className="field-input"
              placeholder="例如：北京市朝阳区 xx 大厦"
              value={locationText}
              onChange={(e) => setLocationText(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="sos-note">
              情况说明（可选）
            </label>
            <textarea
              id="sos-note"
              className="field-input"
              rows={3}
              placeholder="例如：胸闷、血压 180/110，已含服降压药"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <Button block variant="danger" disabled={trigger.isPending} onClick={() => trigger.mutate()}>
            {trigger.isPending ? '正在派单…' : '一键触发应急响应'}
          </Button>

          <div className="field-hint">
            若情况危急，请同时拨打 120。本平台负责协调合作医疗资源与专家，不替代急救系统。
          </div>
        </>
      )}
    </Sheet>
  );
}
